'use client'

import { useEffect, useState } from 'react'
import { useTheme } from 'next-themes'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { MainNav } from '@/components/theme-new/main-nav'
import { BookMeetingButton } from '@/app/(marketing)/components/book-meeting-modal'
import { MEETINGS_PAGE_URL } from '@/app/(marketing)/components/meetings-url'
import { PillArrow } from '@/components/marketing/brand/brand-ui'
import { marketingConfig } from '@/config/marketing'

const demoButtonClass = cn(
  'inline-flex items-center gap-1.5 rounded-full bg-[#0F517B] px-4 py-2 text-sm font-semibold whitespace-nowrap text-white shadow-sm group/pill transition-[background-color,color,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#FFC933] hover:text-[#0F3D5F] hover:shadow-lg hover:shadow-[#0F3D5F]/25 motion-reduce:transition-colors motion-reduce:hover:translate-y-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F517B] sm:px-5',
  'dark:bg-[#F8FAFC] dark:text-[#0F517B] dark:hover:bg-[#FFC933]'
)

interface StickyHeaderProps {
  user: boolean
}

export function StickyHeader({ user }: StickyHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const { theme, resolvedTheme } = useTheme()

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      setIsScrolled(scrollY > 20)
    }

    // Add event listener
    window.addEventListener('scroll', handleScroll, { passive: true })

    // Check initial scroll position
    handleScroll()

    // Cleanup
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <header
      className={cn(
        'fixed left-0 right-0 top-0 z-50 transition-all duration-300 ease-in-out',
        isScrolled ? 'sticky-header-blur border-b border-border/40 bg-background/95 shadow-xs' : 'bg-transparent'
      )}
    >
      {/* Narrower side padding on phones so the logo, menu, and Book a demo fit at 320px. */}
      <div className="container px-4 sm:px-8">
        <div className="flex h-20 items-center justify-between py-6">
          {/* Log in and View dashboard sit in the menu on phones so the header bar fits. */}
          <MainNav items={marketingConfig.mainNav}>
            <Link
              href={user ? '/home' : '/login'}
              className="mt-2 flex w-full items-center rounded-md px-3 py-2 text-sm font-semibold text-[#0F517B] hover:bg-accent lg:hidden dark:text-[#BAD7EC]"
            >
              {user ? 'View dashboard' : 'Log in'}
            </Link>
          </MainNav>
          <nav className="ml-3 flex shrink-0 items-center gap-4 sm:ml-6 sm:gap-5">
            {!user && (
              <Link
                href="/login"
                className="hidden text-sm font-semibold whitespace-nowrap text-[#0F517B] transition-colors hover:text-[#0F4A72] hover:underline lg:inline dark:text-[#BAD7EC] dark:hover:text-white"
              >
                Log in
              </Link>
            )}
            {user && (
              <Link
                href="/home"
                className="hidden text-sm font-semibold whitespace-nowrap text-[#0F517B] transition-colors hover:text-[#0F4A72] hover:underline lg:inline dark:text-[#BAD7EC] dark:hover:text-white"
              >
                View dashboard
              </Link>
            )}
            <BookMeetingButton href={MEETINGS_PAGE_URL} className={demoButtonClass}>
              Book a demo
              <PillArrow className="hidden sm:inline-flex" />
            </BookMeetingButton>
          </nav>
        </div>
      </div>
    </header>
  )
}
